import { useI18n } from '@platform/i18n';
import { breadcrumbFor, Button, Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, ErrorState, Label, localizeRegistry, MenuSelect, PageBody, PageHeader, SettingsCard, Skeleton, useDiscardGuard, useErrorText } from '@platform/ui';
import { useContext, useEffect, useId, useMemo, useState } from 'react';
import { useBlocker, useLocation } from 'react-router';
import { CalendarContext } from './calendar/calendar-context';
import type { RegionalPreferences } from './api/calendar-extra';
import { browserTimeZone } from './calendar/zones';
import { useRegionalSettings } from './regional-settings';
import { webmailNavigation } from './navigation';
import { SettingsTabs } from './pages/settings-tabs';
import { TimeZonePicker } from './time-zone-picker';
import { useAuth } from './auth';

function RegionalForm({ value, firstUse }: { value: RegionalPreferences; firstUse: boolean }) {
  const { t, locale, languages } = useI18n();
  const { save } = useRegionalSettings();
  const { logout } = useAuth();
  const errors = useErrorText();
  const id = useId();
  const [language, setLanguage] = useState(value.locale ?? locale);
  const [zone, setZone] = useState<string | null>(value.displayTimeZone ?? browserTimeZone());
  const [numbering, setNumbering] = useState(value.weekNumbering);
  const [busy, setBusy] = useState(false);
  const [zonePending, setZonePending] = useState(false);
  const dirty = zonePending || language !== (value.locale ?? locale) || zone !== (value.displayTimeZone ?? browserTimeZone()) || numbering !== value.weekNumbering;
  const blocker = useBlocker(dirty || busy);
  const confirmation = useMemo(() => ({ title: t('wizard.discardTitle'), description: t('wizard.discardDescription'), confirmLabel: t('wizard.discard') }), [t]);
  const { request: requestDiscard, prompt: discardPrompt } = useDiscardGuard({ dirty, busy, confirmation });
  useEffect(() => {
    if (blocker.state === 'blocked' && !busy) requestDiscard(() => blocker.proceed(), () => blocker.reset());
  }, [blocker, busy, requestDiscard]);
  const submit = async (defer: boolean) => {
    if (!defer && zonePending) return;
    setBusy(true);
    try { await save(defer ? { ...value, setup: 'deferred' } : { locale: language, displayTimeZone: zone || null, weekNumbering: numbering, setup: 'completed' }); }
    catch (error) { errors.notify(error); }
    finally { setBusy(false); }
  };
  const signOut = () => requestDiscard(() => {
    setBusy(true);
    void logout().catch((error: unknown) => errors.notify(error)).finally(() => setBusy(false));
  });
  return <><form onSubmit={(event) => { event.preventDefault(); void submit(false); }} className="contents">
    <DialogBody className="flex flex-col gap-5">
      <div className="flex flex-col gap-2"><Label>{t('prefs.language')}</Label>
        <MenuSelect label={t('prefs.language')} value={language} onChange={setLanguage} disabled={busy} options={languages.map((entry) => ({ value: entry.locale, label: entry.name }))} />
      </div>
      <TimeZonePicker id={`${id}-zone`} label={t('webmail.regional.timeZone')} value={zone} onChange={setZone} onPendingChange={setZonePending} disabled={busy} />
      <p className="text-xs text-muted-foreground">{t('webmail.calendarUx.settings.displayZoneBrowser', { zone: browserTimeZone() })}</p>
      <div className="flex flex-col gap-2"><Label>{t('webmail.regional.weekNumbering')}</Label>
        <MenuSelect label={t('webmail.regional.weekNumbering')} value={numbering} onChange={setNumbering} disabled={busy} options={[{ value: 'regional', label: t('webmail.regional.regionalWeeks') }, { value: 'iso', label: t('webmail.regional.isoWeeks') }]} />
      </div>
    </DialogBody>
    <DialogFooter>{firstUse && <><Button type="button" variant="outline" disabled={busy} onClick={signOut}>{t('auth.signOut')}</Button><Button type="button" variant="ghost" disabled={busy} onClick={() => void submit(true)}>{t('webmail.regional.later')}</Button></>}<Button type="submit" disabled={busy || zonePending}>{t('common.save')}</Button></DialogFooter>
  </form>{discardPrompt}</>;
}

export function RegionalSetup() {
  const { t } = useI18n();
  const { value } = useRegionalSettings();
  if (value?.setup !== 'pending') return null;
  return <Dialog open><DialogContent framed showCloseButton={false} onEscapeKeyDown={(event) => event.preventDefault()} onInteractOutside={(event) => event.preventDefault()}>
    <DialogHeader><DialogTitle>{t('webmail.regional.setupTitle')}</DialogTitle><DialogDescription>{t('webmail.regional.description')}</DialogDescription></DialogHeader>
    <RegionalForm value={value} firstUse />
  </DialogContent></Dialog>;
}

export function RegionalSettingsPage() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const { value, error, reload } = useRegionalSettings();
  const errors = useErrorText();
  const calendar = useContext(CalendarContext);
  const breadcrumb = breadcrumbFor(localizeRegistry(webmailNavigation, t), pathname);
  // Use the regional response until the shared calendar settings have loaded.
  const current = value && calendar?.settingsLoaded ? { ...value, displayTimeZone: calendar.settings.displayTimeZone, weekNumbering: calendar.settings.weekNumbering } : value;
  return <><PageHeader breadcrumb={breadcrumb} title={t('webmail.regional.title')} tabs={<SettingsTabs active="regional" />} /><PageBody width="form">
    {error ? <ErrorState description={errors.describe(error)} onRetry={reload} /> : !value ? <Skeleton className="h-48 max-w-lg" /> : null}
    {current && <SettingsCard title={t('webmail.regional.title')}><RegionalForm key={JSON.stringify(current)} value={current} firstUse={false} /></SettingsCard>}
  </PageBody></>;
}
